import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { GeolocationService } from '../geolocation/geolocation.service';
import { UsuarioService } from '../usuario/usuario.service';

/**
 * Tipos de moneda soportados
 */
export type CurrencyType = 'PEN' | 'USD';

/**
 * Información de moneda
 */
export interface CurrencyInfo {
  code: CurrencyType;
  symbol: string;
  name: string;
  modalidad: string; // N, CN para Soles, CE para Dólares
}

/**
 * Configuración de monedas disponibles
 */
export const CURRENCIES: { [key in CurrencyType]: CurrencyInfo } = {
  PEN: {
    code: 'PEN',
    symbol: 'S/.',
    name: 'Soles',
    modalidad: 'N' // Por defecto Natural para nacional
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'Dólares',
    modalidad: 'CE' // Comercial Extranjero
  }
};

/**
 * Servicio centralizado para manejo de moneda según geolocalización y usuario.
 *
 * Lógica:
 * - Sin login + Geolocalización PE → Soles (S/.)
 * - Sin login + Geolocalización NO PE → Dólares ($)
 * - Con login + DNI peruano (8 dígitos) → Soles (S/.)
 * - Con login + Documento extranjero → Dólares ($)
 */
@Injectable({
  providedIn: 'root'
})
export class CurrencyService {

  // Moneda activa
  private currencySubject = new BehaviorSubject<CurrencyInfo>(CURRENCIES.PEN);
  public currency$ = this.currencySubject.asObservable();

  // Estado de si es usuario peruano
  private isPeruvianSubject = new BehaviorSubject<boolean>(true);
  public isPeruvian$ = this.isPeruvianSubject.asObservable();

  // Flag para saber si ya se determinó la moneda
  private currencyDetermined = false;

  constructor(
    private geolocationService: GeolocationService,
    private usuarioService: UsuarioService
  ) {
    this.initCurrencyDetection();
  }

  /**
   * Inicializa la detección de moneda basada en geolocalización
   */
  private initCurrencyDetection(): void {
    // Escuchar cambios en la geolocalización
    this.geolocationService.countryCodeObservable.subscribe(countryCode => {
      this.updateCurrencyBasedOnContext(countryCode);
    });
  }

  /**
   * Actualiza la moneda basándose en el contexto (geolocalización + usuario)
   */
  private updateCurrencyBasedOnContext(countryCode: string): void {
    const isLoggedIn = this.usuarioService.existeSesionUsuario();

    if (isLoggedIn) {
      // Usuario logueado: verificar tipo de documento
      const isPeruvianUser = this.checkIfPeruvianUser();
      this.setPeruvianStatus(isPeruvianUser);
    } else {
      // Sin login: basarse en geolocalización
      const isFromPeru = countryCode === 'PE';
      this.setPeruvianStatus(isFromPeru);
    }

    this.currencyDetermined = true;
  }

  /**
   * Verifica si el usuario logueado tiene documento peruano (DNI de 8 dígitos)
   */
  private checkIfPeruvianUser(): boolean {
    const sesion = this.usuarioService.obtenerSesionUsuario();
    const list = sesion?.response?.list;
    if (!sesion || !sesion.response || !list || (list as any[]).length === 0) {
      return true; // Por defecto peruano si no hay datos
    }

    // Obtener datos del usuario
    // Nota: La sesión básica no tiene el documento, habría que obtenerlo del UsuarioBeanResponse
    // Por ahora, asumimos que si está logueado y vino de PE, es peruano
    const countryCode = this.geolocationService.currentCountryCode;
    return countryCode === 'PE';
  }

  /**
   * Establece si el usuario es peruano y actualiza la moneda
   */
  private setPeruvianStatus(isPeruvian: boolean): void {
    this.isPeruvianSubject.next(isPeruvian);

    if (isPeruvian) {
      this.currencySubject.next(CURRENCIES.PEN);
    } else {
      this.currencySubject.next(CURRENCIES.USD);
    }

    // console.log('💰 Moneda actualizada:', isPeruvian ? 'Soles (PEN)' : 'Dólares (USD)');
  }

  /**
   * Obtiene la moneda actual (sincrónico)
   */
  get currentCurrency(): CurrencyInfo {
    return this.currencySubject.value;
  }

  /**
   * Obtiene el símbolo de moneda actual
   */
  get currencySymbol(): string {
    return this.currencySubject.value.symbol;
  }

  /**
   * Obtiene el código de moneda actual (PEN o USD)
   */
  get currencyCode(): CurrencyType {
    return this.currencySubject.value.code;
  }

  /**
   * Verifica si la moneda actual es Soles
   */
  get isSoles(): boolean {
    return this.currencySubject.value.code === 'PEN';
  }

  /**
   * Verifica si la moneda actual es Dólares
   */
  get isDolares(): boolean {
    return this.currencySubject.value.code === 'USD';
  }

  /**
   * Obtiene la modalidad recomendada según la moneda
   * - PEN → 'N' (Natural) o 'CN' (Comercial Nacional)
   * - USD → 'CE' (Comercial Extranjero)
   */
  get recommendedModalidad(): string {
    return this.currencySubject.value.modalidad;
  }

  /**
   * Verifica si el usuario actual es considerado peruano
   */
  get isPeruvian(): boolean {
    return this.isPeruvianSubject.value;
  }

  /**
   * Fuerza la actualización de la moneda (útil después de login/logout)
   */
  refreshCurrency(): void {
    const countryCode = this.geolocationService.currentCountryCode;
    this.updateCurrencyBasedOnContext(countryCode);
  }

  /**
   * Formatea un precio con la moneda actual
   */
  formatPrice(amount: number): string {
    const formatter = new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${this.currencySymbol} ${formatter.format(amount)}`;
  }

  /**
   * Obtiene el precio correcto de las modalidades según la moneda actual
   * @param modalidades Array de modalidades del producto
   * @returns La modalidad que corresponde a la moneda actual
   */
  getModalidadByCurrentCurrency(modalidades: any[]): any {
    if (!modalidades || modalidades.length === 0) {
      return null;
    }

    // console.log('💰 [CURRENCY] Buscando modalidad para moneda:', this.currencyCode, '- isSoles:', this.isSoles);
    // console.log('💰 [CURRENCY] Modalidades disponibles:', modalidades.map(m => m.name));

    let modalidad = null;

    if (this.isSoles) {
      // Para Soles: buscar N, Natural, CN, o Comercial Nacional
      modalidad = modalidades.find(m =>
        m.name === 'N' ||
        m.name === 'Natural' ||
        m.name === 'CN' ||
        m.name === 'Comercial Nacional'
      );
    } else {
      // Para Dólares: buscar CE o Comercial Extranjero
      modalidad = modalidades.find(m =>
        m.name === 'CE' ||
        m.name === 'Comercial Extranjero'
      );
    }

    // Si aún no encuentra, retornar la primera
    const result = modalidad || modalidades[0];
    // console.log('💰 [CURRENCY] Modalidad seleccionada:', result?.name);
    return result;
  }

  /**
   * Determina si una modalidad corresponde a moneda extranjera
   */
  isExtranjeroModalidad(modalidadName: string): boolean {
    return modalidadName === 'CE';
  }

  /**
   * Determina si una modalidad corresponde a moneda nacional
   */
  isNacionalModalidad(modalidadName: string): boolean {
    return modalidadName === 'N' || modalidadName === 'CN';
  }
}
